import { z } from "zod";

import { emailSchema } from "./auth";
import { ORGANIZATION_STATUSES } from "./organizationLifecycle";
import { ENTERPRISE_TENANT_TYPES, type EnterpriseTenantType } from "./tenant";

/**
 * Organizasyon profili — ORTAK çekirdek (CLOS-188 `[050][S01]`).
 *
 * ### Alan SINIFLARI
 *
 * Kart alanları ikiye ayırıyor ve ikisine farklı davranış istiyor:
 * *"resmî unvan, vergi numarası, sicil/büro numarası ve ülke YÜKSEK ETKİLİ alanlardır;
 * belirgin yeniden onay, gerekçe ve ek doğrulama gerektirir"* — diğerleri standart.
 *
 * Sınıf bir SÜS DEĞİL, yük taşıyor: yüksek etkili bir alanı gerekçesiz değiştirmek
 * reddediliyor ve audit kaydı hangi sınıfın değiştiğini yazıyor. Ayrım burada tek yerde
 * duruyor; iki listeye bölünseydi biri güncellenir, diğeri unutulurdu.
 */

/** Yüksek etkili alanlar — resmî kimlik. Değişimi kurumun hukuki kimliğini etkiler. */
export const HIGH_IMPACT_PROFILE_FIELDS = [
  "legalName",
  "taxNumber",
  "registryNumber",
  "country",
] as const;
export type HighImpactProfileField = (typeof HIGH_IMPACT_PROFILE_FIELDS)[number];

/** Standart alanlar — iletişim ve sunum. */
export const STANDARD_PROFILE_FIELDS = [
  "name",
  "phone",
  "email",
  "website",
  "addressLine",
  "logoUrl",
] as const;
export type StandardProfileField = (typeof STANDARD_PROFILE_FIELDS)[number];

export type OrganizationProfileField = HighImpactProfileField | StandardProfileField;

export function isHighImpactField(field: string): field is HighImpactProfileField {
  return (HIGH_IMPACT_PROFILE_FIELDS as readonly string[]).includes(field);
}

/**
 * Türe göre alan matrisi (kartın *"karşı türün alanları gösterilmez veya kaydedilmez"*
 * kriteri).
 *
 * ⚠ `registryNumber` İKİ TÜRDE DE var ama ANLAMI farklı: şirkette ticaret sicil
 * numarası, hukuk bürosunda baro/büro numarası. Ayrı kolonlara bölmek, iki türün asla
 * aynı anda dolmayacağı iki sütun demekti; ayrımı ETİKET taşıyor, şema değil.
 *
 * `taxNumber` yalnız ŞİRKETTE: hukuk bürosu adapter'ı (CLOS-72) onu istemiyor ve
 * gösterilmeyen bir alanı kaydetmek, kullanıcının hiç görmediği bir veriyi tutmak olurdu.
 */
export const PROFILE_FIELDS_BY_TYPE: Record<EnterpriseTenantType, OrganizationProfileField[]> = {
  enterprise_company: [
    "name",
    "legalName",
    "taxNumber",
    "registryNumber",
    "country",
    "phone",
    "email",
    "website",
    "addressLine",
    "logoUrl",
  ],
  enterprise_law_firm: [
    "name",
    "legalName",
    "registryNumber",
    "country",
    "phone",
    "email",
    "website",
    "addressLine",
    "logoUrl",
  ],
};

/** Türe göre `registryNumber`'ın kullanıcıya görünen adı. */
export const REGISTRY_FIELD_LABELS: Record<EnterpriseTenantType, string> = {
  enterprise_company: "Ticaret sicil numarası",
  enterprise_law_firm: "Baro/büro numarası",
};

export function profileFieldsFor(type: EnterpriseTenantType): OrganizationProfileField[] {
  return PROFILE_FIELDS_BY_TYPE[type];
}

/** ISO 3166-1 alpha-2. Serbest metin, bir gün "TR" ile "Türkiye"yi yan yana getirirdi. */
const countrySchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]{2}$/, "Ülke iki harfli ülke kodu olmalıdır");

export const organizationProfileSchema = z.object({
  tenantId: z.uuid(),
  type: z.enum(ENTERPRISE_TENANT_TYPES),
  name: z.string(),
  legalName: z.string().nullable(),
  taxNumber: z.string().nullable(),
  registryNumber: z.string().nullable(),
  country: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  website: z.string().nullable(),
  addressLine: z.string().nullable(),
  logoUrl: z.string().nullable(),
  /** Görünen alanlar ve etiketler SUNUCUDAN: arayüz kendi matrisini tutmaz. */
  editableFields: z.array(z.string()),
  highImpactFields: z.array(z.string()),
  registryLabel: z.string(),
  /**
   * OPERASYON durumu (CLOS-195 `[054]`) — abonelik/lisanstan AYRI eksen.
   *
   * Profil yanıtında çünkü sayfa kapalı bir organizasyonda düzenlemeyi hiç sunmamalı;
   * ayrı bir uçtan sorulsaydı sayfa iki isteğin yarışında bir an düzenlenebilir görünürdü.
   */
  operationalStatus: z.enum(ORGANIZATION_STATUSES),
  /**
   * Eşzamanlılık sürümü (kartın *"paralel güncelleme kaybolan değişiklik üretmez"*
   * kriteri). İstemci okuduğu sürümü geri gönderiyor; arada başkası kaydettiyse
   * güncelleme reddediliyor.
   */
  version: z.number().int().nonnegative(),
});
export type OrganizationProfile = z.infer<typeof organizationProfileSchema>;

const optionalText = (max: number, label: string) =>
  z.string().trim().max(max, `${label} çok uzun`).nullable().optional();

export const updateOrganizationProfileSchema = z.object({
  /**
   * ⚠ ZORUNLU. Okunan sürüm geri gönderilmezse eşzamanlılık kontrolü yapılamaz ve
   * kartın "kaybolan değişiklik üretmez" kriteri sessizce boş geçilirdi.
   */
  version: z.number({ error: "Sürüm bilgisi eksik" }).int().nonnegative(),

  name: z
    .string()
    .trim()
    .min(2, "Ad en az 2 karakter olmalıdır")
    .max(200, "Ad çok uzun")
    .optional(),
  legalName: optionalText(300, "Resmî unvan"),
  taxNumber: optionalText(40, "Vergi numarası"),
  registryNumber: optionalText(60, "Sicil numarası"),
  country: countrySchema.nullable().optional(),
  phone: z
    .string()
    .trim()
    .max(32, "Telefon çok uzun")
    .regex(/^[0-9+()\s-]*$/, "Telefon yalnız rakam ve + ( ) - içerebilir")
    .nullable()
    .optional(),
  email: emailSchema.nullable().optional(),
  // `[017]`'nin denetim bulgusuyla aynı: `z.url()` tek başına `javascript:` şemasını da
  // kabul ediyordu ve saklanan değer bir gün bağlantı olarak render edilince XSS olurdu.
  website: z
    .url({ protocol: /^https?$/, error: "Adres http(s) olmalıdır" })
    .max(2048, "Adres çok uzun")
    .nullable()
    .optional(),
  addressLine: optionalText(400, "Adres"),
  logoUrl: z
    .url({ protocol: /^https?$/, error: "Adres http(s) olmalıdır" })
    .max(2048, "Adres çok uzun")
    .nullable()
    .optional(),

  /**
   * Yüksek etkili alan değişiyorsa ZORUNLU (kartın *"gerekçe"* kriteri).
   *
   * Gerekçe audit'e giriyor: "vergi numarası neden değişti?" sorusunun cevabı, değişikliği
   * yapan kişinin o an yazdığı cümledir — sonradan hatırlanmaz.
   */
  justification: z
    .string()
    .trim()
    .min(10, "Gerekçe en az 10 karakter olmalıdır")
    .max(500)
    .optional(),
});
export type UpdateOrganizationProfileInput = z.infer<typeof updateOrganizationProfileSchema>;
